import { Component, DestroyRef, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FilledButtonComponent, OutlinedButtonComponent } from '@custom-components/general/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent, FilterGroup } from '@custom-components/general/table-filter-bar';
import { partnerFilterGroup } from '@core/utils/partner-filter-group.util';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import {
  APP_PATHS,
  COUNTRIES,
  LOCATIONS_TABLE_COLUMNS_CONFIG,
  PAGINATION_DEFAULT,
  XEMEX_OID,
} from '@core/constants';
import { LocationModel, LocationPayloadModel, getUserActivePartner } from '@core/models';
import { LocationStore } from '@root/app/store/location.store';
import { UserStore } from '@root/app/store/user.store';
import { CompanyService } from '@core/services';
import { ImportLocationsDialogComponent, LocationDialogComponent } from '../../components';

@Component({
  selector: 'xemex-locations-page',
  imports: [
    TableComponent,
    TableFilterBarComponent,
    PageHeaderComponent,
    PageBodyComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    MatIconModule,
    TranslateModule,
  ],
  providers: [LocationStore],
  templateUrl: './locations-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './locations-page.component.scss',
})
export class LocationsPageComponent {
  readonly locationStore = inject(LocationStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly companyService = inject(CompanyService);
  private readonly userStore = inject(UserStore);

  /** The partner pill is only offered to Xemex users, who can see every partner's data. */
  protected readonly isXemexUser = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly columns = LOCATIONS_TABLE_COLUMNS_CONFIG;
  protected readonly tableData = computed(
    () =>
      this.locationStore.locationData().locations.map(loc => ({
        ...loc,
        address: this.getLocationString(loc),
        installerName: loc.partnerName ?? '',
      })) as unknown as Record<string, unknown>[],
  );

  protected readonly totalCount = computed(() => this.locationStore.locationData().numberOfItems);

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    {
      key: 'country',
      label: this.translate.instant('locations.filter.country'),
      options: COUNTRIES.map(c => c.id),
    },
    ...(this.isXemexUser()
      ? [
          partnerFilterGroup(
            this.companyService,
            this.translate.instant('locations.filter.partner'),
            this.translate.instant('general.placeholderInput'),
          ),
        ]
      : []),
  ]);

  /** Deep-linked, e.g. from a partner's detail page — pre-selects the "partner" pill (the
   * filter bar only reads this once, on init). */
  protected readonly initialFilters = computed<Record<string, string[]>>(() => {
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    const filters: Record<string, string[]> = {};
    if (partnerOid && this.isXemexUser()) filters['partner'] = [partnerOid];
    return filters;
  });

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Locations,
    String(row['locationId']),
  ];

  constructor() {
    this.getLocations();
  }

  private getLocations(): void {
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    if (!partnerOid) {
      this.locationStore.getLocationsMethod(PAGINATION_DEFAULT.offset);
      return;
    }
    this.locationStore.getLocationsFilterMethod({ partnerOid });
  }

  private getLocationString(location: LocationModel): string {
    return [
      location.locationStreet,
      location.locationStreetNumber,
      location.locationStreetNumberAddition,
    ]
      .filter(e => e != null && e !== '')
      .join(', ');
  }

  protected onSearchChange(query: string): void {
    this.locationStore.getLocationsFilterMethod({ address: query });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    const partnerOid = filters['partner']?.[0];
    const payload: LocationPayloadModel = {
      country: filters['country']?.join(',') ?? '',
      partnerOid,
    };
    this.locationStore.getLocationsFilterMethod(payload);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { partnerOid: partnerOid ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onPageChange(evt: PageEvent): void {
    this.locationStore.getLocationsMethod(evt.pageIndex);
  }

  protected openAddDialog(): void {
    const ref = this.dialog.open(LocationDialogComponent, {
      panelClass: 'no-padding-dialog',
      width: '44rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (saved) {
          this.locationStore.getLocationsMethod(PAGINATION_DEFAULT.offset);
        }
      });
  }

  protected openImportDialog(): void {
    const ref = this.dialog.open(ImportLocationsDialogComponent, {
      panelClass: 'no-padding-dialog',
      width: '44rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(imported => {
        if (imported) {
          this.locationStore.getLocationsMethod(PAGINATION_DEFAULT.offset);
        }
      });
  }

  protected onRefreshClicked(): void {
    this.locationStore.refreshLocationsMethod();
  }
}
